import {Plural, Trans} from '@lingui/macro';
import {first} from 'lodash';
import React, {FC, memo} from 'react';
import styled from 'styled-components';

import {FrontClickEvent, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ConversationStatusesEnum} from '../../../../../helpers/models/conversationHelpers';
import {isTeamNamespace} from '../../../../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {InboxModel} from '../../../../../models/api/inboxModel';
import {getUserNamespace} from '../../../../../store/data/teammates/teammatesSelectors';

/*
 * Constants.
 */

enum HoverActionsEnum {
  ARCHIVE = 'Archive',
  REOPEN = 'Reopen',
  RESTORE = 'Restore',
}

const tooltipContent = {
  [HoverActionsEnum.ARCHIVE]: {
    user: () => <Trans id="cell-hover-icons-archive-in-my-inbox">Archive in my inbox</Trans>,
    delegated: () => <Trans id="cell-hover-icons-archive-in-teammate-inbox">Archive in teammate inbox</Trans>,
    other: (inboxes: ReadonlyArray<InboxModel>) => {
      const inbox = first(inboxes);
      return (
        <Plural
          id="cell-hover-icons-archive-in-inboxes"
          value={inboxes.length}
          zero="Archive"
          one={`Archive in ${inbox?.name}`}
          other="Archive in # inboxes"
        />
      );
    },
  },
  [HoverActionsEnum.REOPEN]: {
    user: () => <Trans id="cell-hover-icons-reopen-in-my-inbox">Reopen in my inbox</Trans>,
    delegated: () => <Trans id="cell-hover-icons-reopen-in-teammate-inbox">Reopen in teammate inbox</Trans>,
    other: (inboxes: ReadonlyArray<InboxModel>) => {
      const inbox = first(inboxes);
      return (
        <Plural
          id="cell-hover-icons-reopen-in-inboxes"
          value={inboxes.length}
          zero="Reopen"
          one={`Reopen in ${inbox?.name}`}
          other="Reopen in # inboxes"
        />
      );
    },
  },
  [HoverActionsEnum.RESTORE]: {
    user: () => <Trans id="cell-hover-icons-restore-in-my-inbox">Restore in my inbox</Trans>,
    delegated: () => <Trans id="cell-hover-icons-restore-in-teammate-inbox">Restore in teammate inbox</Trans>,
    other: (inboxes: ReadonlyArray<InboxModel>) => {
      const inbox = first(inboxes);
      return (
        <Plural
          id="cell-hover-icons-restore-in-inboxes"
          value={inboxes.length}
          zero="Restore"
          one={`Restore in ${inbox?.name}`}
          other="Restore in # inboxes"
        />
      );
    },
  },
};

/*
 * Props.
 */

interface CellHoverIconsProps {
  onArchiveClick: HandlerOf<FrontClickEvent>;
  onTrashClick: HandlerOf<FrontClickEvent>;
  onOpenClick: HandlerOf<FrontClickEvent>;
  status: ConversationStatusesEnum;
  isSelected?: boolean;
  inboxes: ReadonlyArray<InboxModel>;
  hasReminder: boolean;
  isTeamContext: boolean;
  isDelegated: boolean;
}

/*
 * Style.
 */

export const StyledCellHoverIconsWrapper = styled.div`
  display: none;

  grid-row-start: top-right;
  grid-column-start: top-right;
  grid-row-end: top-right;
  grid-column-end: bottom-right;

  justify-self: center;
  align-self: start;
  flex-direction: column;
  max-width: 30px;
  padding: 2px;
  z-index: 2;
  border-radius: 8px;
  background: ${(p) => p.theme.greys.shade10};
  ${(p) => boxShadow(p.theme, `0px 0px 8px rgba(0, 0, 0, 0.1)`)};
  margin-top: -5px; // Accounts for padding on the icon
`;

interface StyledIconDivProps {
  $hasReminder?: boolean;
}
const StyledIconDiv = styled.div<StyledIconDivProps>`
  height: 100%;
  ${center()};
`;

/*
 * Components.
 */

interface RenderIconTooltipParams {
  inboxes: ReadonlyArray<InboxModel>;
  action: HoverActionsEnum;
  isTeamContext: boolean;
  isDelegated: boolean;
}

function renderIconTooltip(params: RenderIconTooltipParams) {
  const {inboxes, action, isTeamContext, isDelegated} = params;

  if (isDelegated) {
    return tooltipContent[action].delegated();
  }

  return isTeamContext ? tooltipContent[action].other(inboxes) : tooltipContent[action].user();
}

export const CellHoverIcons: FC<CellHoverIconsProps> = memo((props) => {
  const theme = useUserTheme();
  const {isTeamContext, isDelegated} = props;

  const userNamespace = useAppSelector(getUserNamespace);
  const accessibleConversationInboxes = props.inboxes.filter(
    (inbox) => inbox.namespace === userNamespace || isTeamNamespace(inbox.namespace),
  );

  const isArchived = props.status === ConversationStatusesEnum.ARCHIVED;
  const isTrashed = props.status === ConversationStatusesEnum.TRASHED;

  const archiveStandardColor = isArchived ? theme.palette.green.shade40 : theme.greys.shade80;

  const archiveLabel = renderIconTooltip({
    inboxes: accessibleConversationInboxes,
    action: isArchived ? HoverActionsEnum.REOPEN : HoverActionsEnum.ARCHIVE,
    isTeamContext,
    isDelegated,
  });

  // Don't trigger a double click on the cell when we double click on a hover icon.
  const onDoubleClick = (event: FrontClickEvent) => {
    event.preventDefault();
  };

  return (
    <StyledCellHoverIconsWrapper>
      {!props.hasReminder && !isTrashed && (
        <StyledIconDiv>
          <IconButton
            iconName={isArchived ? 'archiveFilledSmall' : 'archiveSmall'}
            onClick={isArchived ? props.onOpenClick : props.onArchiveClick}
            onDoubleClick={onDoubleClick}
            label={archiveLabel}
            // This moves the tooltip label 2px higher
            labelMargin={2}
            color={archiveStandardColor}
            hoverColor={archiveStandardColor}
            backgroundHoverColor={theme.alphas.gray10}
            placement="top"
            size={VisualSizesEnum.SMALL}
          />
        </StyledIconDiv>
      )}
    </StyledCellHoverIconsWrapper>
  );
});
